import { gql } from 'graphql-tag';

// Sponsors operations against the Nexus backend
// Query for fetching the current user's sponsors across all program enrollments

export const MY_SPONSORS_QUERY = gql`
  query MySponsors {
    mySponsors {
      id
      type
      name
      logoUrl
      initials
      firstName
      lastName
      sponsoredAt
      tokensReceived
      programsSupported
      joinLink
      isValidated
      badges {
        name
        badgeType
        count
        earnedAt
      }
      # Cash sponsorship fields
      cashReceived
      messages
      isAnonymous
      sponsorCategory
      paymentCount
      cashPayments {
        amount
        message
        paidAt
      }
      # Public portfolio link (only returned if org has portfolioPublic=true)
      slug
    }
  }
`;

// Mutation for updating organization logo (org members can use this)
export const UPDATE_ORGANIZATION_LOGO_MUTATION = gql`
  mutation UpdateOrganizationLogo($input: UpdateOrganizationLogoInput!) {
    updateOrganizationLogo(input: $input) {
      id
      name
      logo
    }
  }
`;

// Input type for the mutation
export interface UpdateOrganizationLogoInput {
  organizationId: string;
  logoUrl: string;
}

// Response type for the mutation
export interface UpdateOrganizationLogoResponse {
  updateOrganizationLogo: {
    id: string;
    name: string;
    logo: string | null;
  };
}

// Mutation for uploading organization logo image (base64) - secure server-side upload
export const UPLOAD_ORGANIZATION_LOGO_IMAGE_MUTATION = gql`
  mutation UploadOrganizationLogoImage($input: UploadOrganizationLogoImageInput!) {
    uploadOrganizationLogoImage(input: $input) {
      id
      name
      logo
    }
  }
`;

// Input type for the base64 upload mutation
export interface UploadOrganizationLogoImageInput {
  organizationId: string;
  logoImage: string; // base64-encoded image with data URI prefix
}

// Response type for the base64 upload mutation
export interface UploadOrganizationLogoImageResponse {
  uploadOrganizationLogoImage: {
    id: string;
    name: string;
    logo: string | null;
  };
}

// Mutation for updating organization name (org members can use this)
export const UPDATE_ORGANIZATION_NAME_MUTATION = gql`
  mutation UpdateOrganizationName($input: UpdateOrganizationNameInput!) {
    updateOrganizationName(input: $input) {
      id
      name
      logo
    }
  }
`;

// Input type for the name update mutation
export interface UpdateOrganizationNameInput {
  organizationId: string;
  name: string;
}

// Response type for the name update mutation
export interface UpdateOrganizationNameResponse {
  updateOrganizationName: {
    id: string;
    name: string;
    logo: string | null;
  };
}

// ============================================================
// Email Verification Mutations
// ============================================================

/**
 * Send a 6-digit verification code to join an organization
 * No authentication required - public endpoint
 */
export const SEND_ORGANIZATION_VERIFICATION_CODE_MUTATION = gql`
  mutation SendOrganizationVerificationCode($input: SendVerificationCodeInput!) {
    sendOrganizationVerificationCode(input: $input) {
      success
      message
      emailSent
    }
  }
`;

/**
 * Verify the 6-digit code and join an organization
 * No authentication required - public endpoint
 */
export const VERIFY_ORGANIZATION_CODE_MUTATION = gql`
  mutation VerifyOrganizationCode($input: VerifyCodeInput!) {
    verifyOrganizationCode(input: $input) {
      success
      message
      joined
      organizationId
      organizationName
    }
  }
`;

/**
 * Verify the 6-digit code and join an organization (authenticated)
 * Links the verified email to the authenticated user
 */
export const VERIFY_ORGANIZATION_CODE_AUTHENTICATED_MUTATION = gql`
  mutation VerifyOrganizationCodeAuthenticated($input: VerifyCodeInput!) {
    verifyOrganizationCodeAuthenticated(input: $input) {
      success
      message
      joined
      organizationId
      organizationName
    }
  }
`;

// Input types for email verification
export interface SendVerificationCodeInput {
  email: string;
  organizationId: string;
}

export interface VerifyCodeInput {
  email: string;
  organizationId: string;
  code: string;
}

// Response type for verification operations
export interface VerificationResponse {
  success: boolean;
  message?: string;
  emailSent?: boolean;
  joined?: boolean;
  organizationId?: string;
  organizationName?: string;
}

export interface SendVerificationCodeResponse {
  sendOrganizationVerificationCode: VerificationResponse;
}

export interface VerifyCodeResponse {
  verifyOrganizationCode: VerificationResponse;
}

export interface VerifyCodeAuthenticatedResponse {
  verifyOrganizationCodeAuthenticated: VerificationResponse;
}

// ============================================================
// Domain Management Operations
// ============================================================

/**
 * Query: Get organization with domain details
 * Requires authentication and membership
 */
export const ORGANIZATION_DOMAINS_QUERY = gql`
  query OrganizationDomains($organizationId: String!) {
    organizationDomains(organizationId: $organizationId) {
      id
      name
      domains
      primaryDomain
      domainVerifications {
        domain
        verifiedAt
        verifiedBy
        verifierEmail
      }
    }
  }
`;

/**
 * Mutation: Remove a domain from an organization
 * Requires authentication and admin/owner role
 */
export const REMOVE_ORGANIZATION_DOMAIN_MUTATION = gql`
  mutation RemoveOrganizationDomain($input: ManageOrganizationDomainInput!) {
    removeOrganizationDomain(input: $input) {
      success
      message
      domains
      primaryDomain
    }
  }
`;

/**
 * Mutation: Set the primary domain for an organization
 * Requires authentication and admin/owner role
 */
export const SET_ORGANIZATION_PRIMARY_DOMAIN_MUTATION = gql`
  mutation SetOrganizationPrimaryDomain($input: ManageOrganizationDomainInput!) {
    setOrganizationPrimaryDomain(input: $input) {
      success
      message
      domains
      primaryDomain
    }
  }
`;

/**
 * Mutation: Add a domain after email verification
 * Requires authentication and admin/owner role
 */
export const ADD_ORGANIZATION_DOMAIN_AFTER_VERIFICATION_MUTATION = gql`
  mutation AddOrganizationDomainAfterVerification(
    $input: AddOrganizationDomainAfterVerificationInput!
  ) {
    addOrganizationDomainAfterVerification(input: $input) {
      success
      message
      domains
      primaryDomain
    }
  }
`;

// Input type for adding a domain after verification
export interface AddOrganizationDomainAfterVerificationInput {
  organizationId: string;
  domain: string;
  verifierEmail: string;
}

// Input type for domain management
export interface ManageOrganizationDomainInput {
  organizationId: string;
  domain: string;
}

// Response type for domain management operations
export interface DomainManagementResponse {
  success: boolean;
  message: string;
  domains?: string[];
  primaryDomain?: string;
}

export interface RemoveOrganizationDomainResponse {
  removeOrganizationDomain: DomainManagementResponse;
}

export interface SetOrganizationPrimaryDomainResponse {
  setOrganizationPrimaryDomain: DomainManagementResponse;
}

export interface AddOrganizationDomainAfterVerificationResponse {
  addOrganizationDomainAfterVerification: DomainManagementResponse;
}

// Domain verification info from Organization
export interface DomainVerification {
  domain: string;
  verifiedAt: string;
  verifiedBy: string;
  verifierEmail: string;
}

export interface OrganizationDomainsResponse {
  organizationDomains: {
    id: string;
    name: string;
    domains: string[];
    primaryDomain: string | null;
    domainVerifications: DomainVerification[];
  };
}

// ============================================================
// Organization Discovery Operations
// ============================================================

/**
 * Query: Find organizations that match a given email domain
 *
 * @requires Authentication - User must be logged in
 * @description Returns organizations matching the user's email domain,
 *              automatically filtering out organizations the user has already joined.
 *              Used by "Create Organization" form and wallet selector to show
 *              organizations the user can join.
 *              Super-admins (@pilotcity.com) can access all organizations:
 *              - Without search: returns first 100 orgs alphabetically
 *              - With search: returns all matching orgs (no limit)
 * @returns OrganizationByDomain[] - Lightweight org objects (id, name, logo, domains)
 * @note Generic email domains (gmail.com, etc.) return empty array
 */
export const ORGANIZATIONS_BY_DOMAIN_QUERY = gql`
  query OrganizationsByDomain($domain: String!, $search: String) {
    organizationsByDomain(domain: $domain, search: $search) {
      id
      name
      logo
      domains
    }
  }
`;

// Response type for organizations by domain query
export interface OrganizationByDomain {
  id: string;
  name: string;
  logo: string | null;
  domains: string[];
}

export interface OrganizationsByDomainResponse {
  organizationsByDomain: OrganizationByDomain[];
}

// ============================================================
// Stakeholder Invitation Operations
// ============================================================

/**
 * Mutation: Cancel a pending stakeholder invitation
 * Requires authentication and admin/owner role
 */
export const CANCEL_STAKEHOLDER_INVITATION_MUTATION = gql`
  mutation CancelStakeholderInvitation($input: CancelInvitationInput!) {
    cancelStakeholderInvitation(input: $input) {
      success
      message
    }
  }
`;

// Input type for cancel invitation
export interface CancelInvitationInput {
  organizationId: string;
  invitee: string;
}

// Response type for cancel invitation
export interface CancelInvitationResponse {
  success: boolean;
  message: string;
}

export interface CancelStakeholderInvitationResponse {
  cancelStakeholderInvitation: CancelInvitationResponse;
}

/**
 * Mutation: Remove a member from an organization
 * Requires authentication and admin/owner role
 */
export const REMOVE_ORGANIZATION_MEMBER_MUTATION = gql`
  mutation RemoveOrganizationMember($input: RemoveOrganizationMemberInput!) {
    removeOrganizationMember(input: $input) {
      success
      message
    }
  }
`;

// Input type for removing organization member
export interface RemoveOrganizationMemberInput {
  organizationId: string;
  userId: string;
}

// Response type for remove member (same structure as cancel invitation)
export interface RemoveOrganizationMemberResponse {
  removeOrganizationMember: CancelInvitationResponse;
}

// ============================================================
// My Organizations Query (with domain fields)
// ============================================================

/**
 * Query: Get all organizations where the current user is a member
 *
 * @requires Authentication - User must be logged in
 * @description Returns all organizations where the user is in the users array.
 *              Includes domain fields (domains, primaryDomain, domainVerifications)
 *              for domain-gating functionality in team invites.
 * @returns Organization[] - Full org objects with domain and user data
 */
export const MY_ORGANIZATIONS_QUERY = gql`
  query MyOrganizations {
    myOrganizations {
      id
      name
      logo
      created
      updated
      domains
      primaryDomain
      slug
      portfolioPublic
      domainVerifications {
        domain
        verifiedAt
        verifiedBy
        verifierEmail
      }
      users {
        user_id
        role
        firstName
        lastName
        email
        created
        updated
      }
      invitationLink
      isAutoSponsoring
      isShareCodeAdded
      sponsorLinkSharable
      created_by
    }
  }
`;

// Type for organization users
export interface OrganizationUser {
  user_id: string;
  role: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  created?: string;
  updated?: string;
}

// Response type for myOrganizations query
export interface MyOrganization {
  id: string;
  name: string;
  logo: string | null;
  created: string;
  updated: string;
  domains: string[];
  primaryDomain: string | null;
  slug: string | null;
  portfolioPublic: boolean | null;
  domainVerifications: DomainVerification[];
  users: OrganizationUser[];
  invitationLink: string | null;
  isAutoSponsoring: boolean | null;
  isShareCodeAdded: boolean | null;
  sponsorLinkSharable: boolean | null;
  created_by: string | null;
}

export interface MyOrganizationsResponse {
  myOrganizations: MyOrganization[];
}

// ============================================================
// Sponsor Link Operations (Single Source of Truth)
// ============================================================

/**
 * Query: Get or auto-generate sponsor link for current user
 *
 * @requires Authentication - User must be logged in
 * @description Gets the sponsor link from SponsorLinks collection (single source of truth).
 *              If no link exists, it's automatically generated.
 *              Supports both personal wallet links and organization links.
 * @param organizationId - Optional organization ID. If null, returns personal wallet link.
 * @returns SponsorLink - The sponsor link with shareCode
 */
export const MY_SPONSOR_LINK_QUERY = gql`
  query MySponsorLink($organizationId: String) {
    mySponsorLink(organizationId: $organizationId) {
      id
      shareCode
      createdBy
      userId
      type
    }
  }
`;

// Response type for sponsor link
export interface SponsorLink {
  id: string;
  shareCode: string;
  createdBy: string;
  userId: string | null;
  type: string;
}

export interface MySponsorLinkResponse {
  mySponsorLink: SponsorLink;
}

// Query for fetching organization participants (sponsored students)
// Used by OrgTeamBanner for team counts (needs validatedBy for credentials issued count)
export const ORGANIZATION_PARTICIPANTS_QUERY = gql`
  query OrganizationParticipants($organizationId: String!, $limit: Int, $offset: Int) {
    organizationParticipants(organizationId: $organizationId, limit: $limit, offset: $offset) {
      participants {
        id
        participantId
        firstName
        lastName
        programName
        sponsorId
        sponsorName
        tokensReceived
        sponsoredAt
        validatedBy
        cashSponsorInfo {
          email
        }
      }
      totalCount
      hasMore
    }
  }
`;

export interface OrganizationParticipant {
  id: string;
  participantId: string;
  firstName: string | null;
  lastName: string | null;
  programName: string | null;
  sponsorId: string | null;
  sponsorName: string | null;
  tokensReceived: number | null;
  sponsoredAt: string | null;
  validatedBy: string | null;
  cashSponsorInfo: {
    email: string | null;
  } | null;
}

export interface OrganizationParticipantsResponse {
  organizationParticipants: {
    participants: OrganizationParticipant[];
    totalCount: number;
    hasMore: boolean;
  };
}

// ============================================================
// Domain User Discovery Operations
// ============================================================

/**
 * Query: Discover users whose email matches org's verified domains
 *
 * @requires Authentication - User must be logged in and org member
 * @description Returns existing PilotCity users that can be invited
 *              based on matching email domains. Excludes already-joined
 *              members and pending invitations.
 */
export const DISCOVER_DOMAIN_USERS_QUERY = gql`
  query DiscoverDomainUsers($organizationId: String!, $search: String, $limit: Int) {
    discoverDomainUsers(organizationId: $organizationId, search: $search, limit: $limit) {
      users {
        id
        firstName
        lastName
        email
        profileImg
        domain
        isPrimaryDomain
      }
      totalCount
    }
  }
`;

// Response types for domain user discovery
export interface DiscoveredDomainUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  profileImg: string | null;
  domain: string;
  isPrimaryDomain: boolean;
}

export interface DiscoverDomainUsersResponse {
  discoverDomainUsers: {
    users: DiscoveredDomainUser[];
    totalCount: number;
  };
}

// ============================================================
// User Program Counts Operations
// ============================================================

/**
 * Query: Get program counts for multiple users in a batch
 *
 * @requires Authentication - User must be logged in
 * @description Returns program counts for Team table columns:
 *              - programsCreated: Programs where user is creator OR organizer
 *              - programsParticipated: Distinct programs user has participated in
 *              Counts are GLOBAL (not filtered by organization).
 */
export const USER_PROGRAM_COUNTS_QUERY = gql`
  query UserProgramCounts($userIds: [String!]!) {
    userProgramCounts(userIds: $userIds) {
      counts {
        userId
        programsCreated
        programsParticipated
      }
    }
  }
`;

// Response types for user program counts
export interface UserProgramCount {
  userId: string;
  programsCreated: number;
  programsParticipated: number;
}

export interface UserProgramCountsResponse {
  userProgramCounts: {
    counts: UserProgramCount[];
  };
}

// ============================================================
// Sponsor Link By Share Code (Public Landing Page)
// ============================================================

/**
 * Query: Get sponsor link by share code (public, no auth required)
 *
 * @description Fetches sponsor link data from MongoDB for landing pages.
 *              This is a public query that doesn't require authentication.
 *              Used by the landing page to display sponsor settings.
 * @param shareCode - The share code to look up
 * @returns SponsorLinkOutput - The sponsor link with settings
 */
export const SPONSOR_LINK_BY_SHARE_CODE_QUERY = gql`
  query SponsorLinkByShareCode($shareCode: String!) {
    sponsorLinkByShareCode(shareCode: $shareCode) {
      id
      shareCode
      createdBy
      userId
      type
      programSettings
    }
  }
`;

// Response type for sponsor link by share code
export interface SponsorLinkByShareCodeResponse {
  sponsorLinkByShareCode: {
    id: string;
    shareCode: string;
    createdBy: string;
    userId: string | null;
    type: string;
    programSettings?: Record<string, unknown>;
  } | null;
}

/**
 * Public query to get basic organization info by ID
 * Used by the sponsor landing page to display organization name/logo
 *
 * Authorization: None (public)
 *
 * @param organizationId - The organization ID to look up
 * @returns Organization basic info (id, name, logo, domains)
 */
export const ORGANIZATION_BASIC_INFO_QUERY = gql`
  query OrganizationBasicInfo($organizationId: String!) {
    organizationBasicInfo(organizationId: $organizationId) {
      id
      name
      logo
      domains
    }
  }
`;

// Response type for organization basic info
export interface OrganizationBasicInfoResponse {
  organizationBasicInfo: {
    id: string;
    name: string;
    logo: string | null;
    domains: string[];
  } | null;
}

// ============================================================
// ShareCode Management Operations
// ============================================================

/**
 * Query: Check if a shareCode is available for use
 *
 * @requires Authentication - User must be logged in
 * @description Validates format, checks reserved words, and verifies uniqueness.
 *              Returns availability status with reason if not available.
 * @param shareCode - The shareCode to check
 * @returns ShareCodeAvailability - { available: boolean, reason?: string }
 */
export const CHECK_SHARE_CODE_AVAILABILITY_QUERY = gql`
  query CheckShareCodeAvailability($shareCode: String!) {
    checkShareCodeAvailability(shareCode: $shareCode) {
      available
      reason
    }
  }
`;

// Response type for shareCode availability check
export interface ShareCodeAvailability {
  available: boolean;
  reason?: string;
}

export interface CheckShareCodeAvailabilityResponse {
  checkShareCodeAvailability: ShareCodeAvailability;
}

/**
 * Mutation: Update a sponsor link's shareCode to a custom value
 *
 * @requires Authentication - User must be logged in
 * @description Updates the shareCode for a sponsor link.
 *              The old shareCode is released immediately and can be claimed by others.
 *              Validates format, checks reserved words, and verifies uniqueness.
 * @param input - { sponsorLinkId, newShareCode, organizationId? }
 * @returns SponsorLink - Updated sponsor link with new shareCode
 */
export const UPDATE_SHARE_CODE_MUTATION = gql`
  mutation UpdateShareCode($input: UpdateShareCodeInput!) {
    updateShareCode(input: $input) {
      id
      shareCode
      createdBy
      userId
      type
    }
  }
`;

/**
 * Mutation: updateSponsorLinkProgramSettings
 *
 * @requires Authentication - link owner, org owner/admin, or @pilotcity.com super-admin
 * @description Persists a sponsor link's full programSettings via Nexus,
 *              replacing the legacy sponsor-service /save-sponsor-setting REST
 *              endpoint. The backend canonicalizes per-activity timeOverrides
 *              (deadline -> dueDate + 'fixed' anchors) so the cascade resolver
 *              and AIPM can consume them.
 */
export const UPDATE_SPONSOR_LINK_PROGRAM_SETTINGS_MUTATION = gql`
  mutation UpdateSponsorLinkProgramSettings($shareCode: String!, $programSettings: JSON!) {
    updateSponsorLinkProgramSettings(shareCode: $shareCode, programSettings: $programSettings) {
      id
      shareCode
      programSettings
    }
  }
`;

// Input type for updateShareCode mutation
export interface UpdateShareCodeInput {
  sponsorLinkId: string;
  newShareCode: string;
  organizationId?: string;
}

// Response type for updateShareCode mutation
export interface UpdateShareCodeResponse {
  updateShareCode: {
    id: string;
    shareCode: string;
    createdBy: string;
    userId: string | null;
    type: string;
  };
}

// ============================================================================
// Owner-on-behalf member sponsor link (auto-creates on first lookup)
// ============================================================================

/**
 * Query: memberSponsorLink
 *
 * Fetch (or auto-generate) the org sponsor link for any member of an
 * organization. Owners/admins use this to manage sponsor links on members' behalf.
 *
 * @param organizationId
 * @param memberId — userId of the org member whose link we're looking up
 * @returns SponsorLink (auto-created on first call if missing)
 */
export const MEMBER_SPONSOR_LINK_QUERY = gql`
  query MemberSponsorLink($organizationId: String!, $memberId: String!) {
    memberSponsorLink(organizationId: $organizationId, memberId: $memberId) {
      id
      shareCode
      createdBy
      userId
      type
    }
  }
`;

export interface MemberSponsorLinkResponse {
  memberSponsorLink: {
    id: string;
    shareCode: string;
    createdBy: string;
    userId: string | null;
    type: string;
  };
}

// ============================================================================
// Organization Team (Public)
// ============================================================================

/**
 * Query for fetching organization team members.
 * Public if organization has portfolioPublic=true.
 */
export const ORGANIZATION_TEAM_QUERY = gql`
  query OrganizationTeam($organizationId: String!) {
    organizationTeam(organizationId: $organizationId) {
      members {
        userId
        slug
        role
        firstName
        lastName
        profileImg
        initials
      }
      isOwner
    }
  }
`;

// Team member returned by organizationTeam query
export interface OrganizationTeamMember {
  userId: string;
  /** User slug for public portfolio link (only returned if user has portfolioPublic=true) */
  slug: string | null;
  role: string;
  firstName: string | null;
  lastName: string | null;
  profileImg: string | null;
  initials: string;
}

// Response type for organizationTeam query
export interface OrganizationTeamResponse {
  members: OrganizationTeamMember[];
  isOwner: boolean;
}

// Full query result type
export interface OrganizationTeamQueryResult {
  organizationTeam: OrganizationTeamResponse | null;
}

// ============================================================================
// Access Codes (SPONSOR_ACCESS_CODES) — secret sub-codes + public-code switch
// ============================================================================

const ACCESS_CODES_FIELDS = `
  sponsorLinkId
  shareCode
  publicCodeEnabled
  codes {
    code
    label
    active
    createdAt
    maxUses
    capMode
    expiresAt
    usedCount
  }
`;

export const SPONSOR_LINK_ACCESS_CODES_QUERY = gql`
  query SponsorLinkAccessCodes($sponsorLinkId: String!, $organizationId: String) {
    sponsorLinkAccessCodes(sponsorLinkId: $sponsorLinkId, organizationId: $organizationId) {
      ${ACCESS_CODES_FIELDS}
    }
  }
`;

export const GENERATE_ACCESS_CODE_MUTATION = gql`
  mutation GenerateAccessCode($input: GenerateAccessCodeInput!) {
    generateAccessCode(input: $input) { ${ACCESS_CODES_FIELDS} }
  }
`;

export const UPDATE_ACCESS_CODE_MUTATION = gql`
  mutation UpdateAccessCode($input: UpdateAccessCodeInput!) {
    updateAccessCode(input: $input) { ${ACCESS_CODES_FIELDS} }
  }
`;

export const DELETE_ACCESS_CODE_MUTATION = gql`
  mutation DeleteAccessCode($input: DeleteAccessCodeInput!) {
    deleteAccessCode(input: $input) { ${ACCESS_CODES_FIELDS} }
  }
`;

export const SET_PUBLIC_CODE_ENABLED_MUTATION = gql`
  mutation SetPublicCodeEnabled($input: SetPublicCodeEnabledInput!) {
    setPublicCodeEnabled(input: $input) { ${ACCESS_CODES_FIELDS} }
  }
`;

export interface AccessCode {
  code: string;
  label: string | null;
  active: boolean;
  createdAt: number | null;
  maxUses: number | null;
  capMode: string | null;
  expiresAt: number | null;
  usedCount: number;
}

export interface AccessCodesResult {
  sponsorLinkId: string;
  shareCode: string;
  publicCodeEnabled: boolean;
  codes: AccessCode[];
}

export interface SponsorLinkAccessCodesResponse {
  sponsorLinkAccessCodes: AccessCodesResult;
}
export interface GenerateAccessCodeResponse { generateAccessCode: AccessCodesResult; }
export interface UpdateAccessCodeResponse { updateAccessCode: AccessCodesResult; }
export interface DeleteAccessCodeResponse { deleteAccessCode: AccessCodesResult; }
export interface SetPublicCodeEnabledResponse { setPublicCodeEnabled: AccessCodesResult; }
